import { useMemo, useRef, useState } from "react";
import { Flex, HStack, VStack } from "../../../styled-system/jsx";
import { Controller, FormProvider, useForm } from "react-hook-form";
import ProgramsForm from "components/custom/programs/ProgramsForm";
import { ProgramContextProvider } from "components/custom/programs/ProgramContext";
import { PROGRAM_DEFAULTS } from "components/custom/programs/constants";
import CenteredModal from "components/core/basic/CenteredModal";
import { useFetch } from "hooks/api";
import { useDates } from "dates/DatesContext";
import { useAppSelector } from "redux/hooks";
import { Button } from "components/core/basic/Button";
import Box from "components/core/basic/Box";
import { HiddenField, InputField, Label } from "components/custom/programs/fields";
import useMerchandisePreferences from "hooks/useMerchandisePreferences";
import BulkActionsTable from "views/merchandise/tables/BulkActionsTable";
import acosOrRoas from "utils/acosOrRoas";
import { buildDateColumn } from "views/merchandise/columns/buildDateColumn";
import { css } from "../../../styled-system/css";
import useDefaultState from "hooks/useDefaultState";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "utils/api";
import { AdvertisementCell } from "modules/advertising/tableCells/AdvertisementCell";
import { successToast } from "../../utils/toasts";
import {
  createColumnHelper,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Table } from "components/table/Table";
import moment from "moment";
import TrellisLink from "components/core/basic/TrellisLink";
import { ButtonGroup } from "components/core/basic/ButtonGroup";
import { RT8Pagination } from "components/table/Pagination";
import { usePersistedTableState } from "components/table/usePersistedTableState";
import { apiClient } from "zodios/client";
import { RT8ColumnSelector } from "components/core/blocks/MetricSelector";
import { SearchInput } from "components/fields/base/TextField";
import { MdRotateLeft } from "react-icons/md";
import { RT8FilterWidget } from "views/merchandise/tables/FilterWidget";
import { resetTable, selectionStateToSelectedIds } from "components/table/utils";
import { BulkActionButton } from "components/table/BulkActionButton";
import { Switch } from "components/fields/base/Switch";
import { FaClone } from "react-icons/fa6";

const useAds = (categoryId = null) => {
  const { group, start, end, preStart, preEnd } = useDates();
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const filters = useMemo(() => {
    const filters = {
      mp: marketPlace.marketPlace,
      date_group: group,
      start_date: start.format("YYYY-MM-DD"),
      end_date: end.format("YYYY-MM-DD"),
      pre_start_date: preStart.format("YYYY-MM-DD"),
      pre_end_date: preEnd.format("YYYY-MM-DD"),
      channel: channel,
      group_by: "ad_id",
      empty: true,
    };

    if (categoryId) {
      filters.category = categoryId;
    }
    return filters;
  }, [marketPlace, group, start, end, preStart, preEnd, channel, categoryId]);

  const {
    data: adPlans,
    isLoading: isAdDataLoading,
    label = "Manage Media Plans",
  } = useFetch(
    ["ad_report", marketPlace.marketPlace, start, end, preStart, preEnd, group, categoryId],
    `/api/data_report/ad_report`,
    { ...filters },
    {
      select: (d) => d.data?.adplans || [],
      keepPreviousData: true,
      default: [],
    },
  );
  return {
    adPlans,
    isAdDataLoading,
    label,
  };
};

const AdvertisingSelectorModal = ({ selectedAdvertisementIds, onAdvertisementChange, categoryId = null }) => {
  const [show, setShow] = useState(false);
  const [selected, setSelected] = useDefaultState(selectedAdvertisementIds);
  const { preferences } = useMerchandisePreferences();
  const tableRef = useRef();

  const { adPlans, isAdDataLoading, label = "Manage Media Plans" } = useAds(categoryId);

  const selectedAdvertisements = useMemo(() => {
    return adPlans?.filter((ad) => selectedAdvertisementIds?.includes(ad.ad_id)) || [];
  }, [adPlans, selectedAdvertisementIds]);

  const columns = [
    {
      key: "daily_budget",
      compareKey: null,
      options: {
        editCell: false,
        columnTitle: "Daily Budget",
        className: "text-end",
      },
    },
    { key: "ad_sales", compareKey: "ad_sales_percent" },
    { key: "ad_spend", compareKey: "ad_spend_percent" },
    {
      key: "roas",
      compareKey: "roas_delta",
      options: {
        checked: acosOrRoas(preferences, false, true),
      },
    },
    {
      key: "acos",
      compareKey: "acos_delta",
      options: {
        checked: acosOrRoas(preferences, true, false),
        isNegative: true,
      },
    },
    { key: "clicks", compareKey: "clicks_percent" },
    { key: "impressions", compareKey: "impressions_percent" },
    buildDateColumn("created_datetime", "Created On"),
  ];

  return (
    <>
      <CenteredModal show={show} closeModal={() => setShow(false)} title={label} size="lg">
        <VStack width="full">
          <div className={css({ width: "full" })}>
            <BulkActionsTable
              tableRef={tableRef}
              tableId="advertisements-select"
              entityType="mediaPlan"
              data={adPlans}
              getSearchCriteria={(row) => {
                const { ad_name, id } = row;
                return `${ad_name} ${id}`;
              }}
              isExpandable={false}
              defaultPageSize={5}
              defaultSorted={[{ id: "ad_sales", desc: true }]}
              isLoading={isAdDataLoading}
              isSelectable={true}
              selected={selected}
              setSelected={setSelected}
              selectionKey="ad_id"
              titleCol={{
                id: "advertisement",
                isStatic: true,
                Header: "Advertisements",
                accessor: "name",
                width: 200,
                Cell: ({ row }) => {
                  return (
                    <AdvertisementCell
                      ad={{
                        ...row._original,
                        id: row._original.ad_id,
                      }}
                    />
                  );
                },
              }}
              columnSpecs={columns}
            />
          </div>
          <HStack justifyContent="end" gap={"2rem"} width="full">
            <Button
              variant="secondary"
              isDisabled={isAdDataLoading}
              onPress={() => {
                setShow(false);
                setSelected(null);
              }}
            >
              Cancel
            </Button>
            <Button
              isDisabled={!selected || selected?.length === 0 || isAdDataLoading}
              onPress={() => {
                setShow(false);
                onAdvertisementChange(selected);
                setSelected(null);
              }}
            >
              Select
            </Button>
          </HStack>
        </VStack>
      </CenteredModal>
      <Box
        empty={selectedAdvertisements?.length === 0}
        emptyMessage={"No Media Plans selected"}
        isPending={isAdDataLoading}
      >
        <VStack gap={4} justifyContent="start">
          <VStack gap={2} width="full" justifyContent="start" maxHeight={"260px"} overflowY={"auto"}>
            {selectedAdvertisements.map((ad) => (
              <div key={ad.ad_id} className={css({ width: "full" })}>
                <AdvertisementCell ad={{ ...ad, id: ad.ad_id }} />
              </div>
            ))}
          </VStack>
        </VStack>
      </Box>
      <HStack justifyContent="end" width="full">
        <Button
          isDisabled={isAdDataLoading}
          onPress={() => {
            setShow(true);
          }}
        >
          {label}
        </Button>
      </HStack>
    </>
  );
};

const HarvestingRulesTable2 = ({ advertisement = null, onClick, onClone, children }) => {
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const defaultSelectedColumns = [
    "program__name",
    "is_enabled",
    "program__status",
    "sources_count",
    "targets_count",
    "created_on",
    "program__last_run",
    "program__last_action",
    "program__run_cnt",
    "actions",
  ];
  if (advertisement) {
    defaultSelectedColumns.splice(2, 0, "role");
  }
  const { setters, state, initialState } = usePersistedTableState({
    persistKey: `harvesters${advertisement ? `_${advertisement.id}` : ""}`,
    initialState: {
      columnOrder: defaultSelectedColumns,
      columnPinning: {
        left: ["progran__name"],
      },
      sorting: [{ id: "program__name" }],
    },
  });

  const harvesterQuery = useQuery({
    queryKey: [
      "harvesters",
      advertisement?.id,
      state.pagination.pageIndex,
      state.pagination.pageSize,
      state.sorting,
      state.globalFilter,
      marketPlace,
    ],
    queryFn: async () => {
      const ordering = state.sorting?.[0] || { id: "name", desc: false };
      const filters = {
        page: state.pagination.pageIndex + 1,
        limit: state.pagination.pageSize,
        ordering: `${ordering.desc ? "-" : ""}${ordering.id}`,
        market_place: marketPlace,
      };
      if (state.globalFilter) {
        filters.search = state.globalFilter;
      }
      if (advertisement) {
        filters.ad = advertisement?.id;
      }
      return await apiClient.getHarvesters({
        queries: filters,
      });
    },
    options: {
      refetchOnMount: true,
    },
  });
  const queryClient = useQueryClient();
  const deleteProgram = useMutation({
    mutationFn: async (ids) => {
      await Promise.all(ids.map(async (id) => api.remove(`/api/harvesters/${id}/`)));
      queryClient.invalidateQueries({
        queryKey: ["harvesters"],
      });
      successToast({ message: "Deleted harvesting rule(s)" });
    },
  });

  // Define columns for the table (example columns, adjust as needed)
  const columns = [
    {
      id: "program__name",
      accessorKey: "program.name",
      header: "Name",
      size: 300,
      minSize: 70,
      cell: ({ getValue, row }) => {
        const name = getValue();
        const id = row.original.id;
        return (
          <div
            className="d-flex align-items-center overflow-ellipsis"
            style={{
              cursor: "pointer",
              color: "#2e0054",
              fontWeight: 500,
              fontSize: "1.5rem",
              height: "5rem",
            }}
            onClick={() => {
              if (onClick) onClick(id);
            }}
          >
            {name}
          </div>
        );
      },
    },
    {
      accessorKey: "is_enabled",
      header: "Enabled",
      cell: function StateCell({ getValue, row }) {
        const id = row.original.id;
        const isEnabled = getValue();
        const [_switchState, setSwitchState] = useState(null);
        const switchState = useMemo(() => {
          if (_switchState === null) {
            return isEnabled;
          }
          return _switchState;
        }, [_switchState, isEnabled]);

        const setEnabled = useMutation({
          mutationFn: async (value) => {
            return await api.patch(`/api/harvesters/${id}/`, { is_enabled: value });
          },
          onSuccess: () => {
            queryClient
              .invalidateQueries({
                queryKey: ["harvesters"],
              })
              .then(() => {
                setSwitchState(null);
              });
            successToast({ message: `Harvesting rule ${switchState ? "enabled" : "disabled"}` });
          },
        });

        return (
          <Switch
            isSelected={switchState}
            isPending={setEnabled.isPending}
            onChange={(value) => {
              setSwitchState(value);
              setEnabled.mutate(value);
            }}
          />
        );
      },
    },
    {
      id: "program__status",
      accessorKey: "program.status",
      header: "Status",
    },
    {
      accessorKey: "sources_count",
      header: "Sources",
    },
    {
      accessorKey: "targets_count",
      header: "Destinations",
    },
    {
      accessorKey: "created_on",
      header: "Created On",
      cell: ({ getValue }) => {
        return moment(getValue()).format("ll");
      },
    },
    {
      id: "program__last_run",
      accessorKey: "program.last_run",
      header: "Last Run",
      cell: ({ getValue }) => {
        return getValue() ? moment(getValue()).format("ll") : "N/A";
      },
    },
    {
      id: "program__last_action",
      accessorKey: "program.last_action",
      header: "Last Action",
      cell: ({ getValue }) => {
        return getValue() ? moment(getValue()).format("ll") : "N/A";
      },
    },
    {
      id: "program__run_cnt",
      accessorKey: "program.run_cnt",
      header: "# Runs",
    },
    {
      id: "actions",
      header: "Actions",
      size: 100,
      minSize: 100,
      cell: ({ row }) => {
        return (
          <HStack gap={2} justifyContent="end">
            <Button
              variant="link"
              onPress={() => {
                const defaults = {
                  ...row.original,
                  id: undefined,
                  program: {
                    ...row.original.program,
                    id: undefined,
                    name: `${row.original.program.name} - Copy`,
                  },
                };
                if (onClick) onClone(defaults);
              }}
            >
              <FaClone />
            </Button>
          </HStack>
        );
      },
    },
  ];

  if (advertisement) {
    // insert the role column after is_enabled
    columns.splice(2, 0, {
      id: "role",
      accessorKey: "role",
      header: "Role",
    });
  }

  const table = useReactTable({
    data: harvesterQuery.data?.results ?? [],
    columns,
    defaultColumn: {
      filterFn: () => true,
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
    getRowId: (row) => row.ad_id?.toString?.() ?? row.id?.toString?.(),
    rowCount: harvesterQuery.data?.count || 0,
    initialState: initialState,
    state: state,
    ...setters,
    manualPagination: true,
    meta: {
      showSkeleton: harvesterQuery.isFetching && (!harvesterQuery.data || harvesterQuery.isPlaceholderData),
      // columnFilterSelectOptions: ... // define if needed
    },
  });
  const selected = selectionStateToSelectedIds(table.getState().rowSelection);
  return (
    <div className={css({ width: "full" })}>
      <HStack justifyContent={"end"} gap={4} padding={"small"} className={css({ width: "full" })}>
        {children}
        <BulkActionButton
          selected={selected}
          options={[
            {
              label: `Delete selected Harvesting Rule${selected?.length > 1 ? "s" : ""}`,
              value: "delete",
              action: () => {
                deleteProgram.mutate(selected);
              },
              confirmationMessage: (
                <p>
                  Are you sure you want to delete the {selected?.length > 1 ? selected?.length : ""} selected harvesting
                  rule
                  {selected?.length > 1 ? "s" : ""}?
                </p>
              ),
            },
          ]}
        />
      </HStack>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={"Search"}
              isExpandable
              className={css({ flex: "0 1 350px" })}
              value={table.getState().globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table table={table} className={css({ minWidth: "100%" })} />
    </div>
  );
};

const HarvestingRulesForm = ({ source, harvester, onCancel, onSave }) => {
  const queryClient = useQueryClient();
  const dt = new Date().toISOString();
  const defaultValues =
    harvester ?
      {
        sources: harvester.sources,
        targets: harvester.targets,
        program: {
          name: harvester.program.name,
          start_date: harvester.program.start_date,
          ...harvester.program.ui_state,
        },
      }
    : {
        sources: source ? [source.id] : [],
        targets: [],
        program: { ...PROGRAM_DEFAULTS["harvesting"], start_date: dt, name: "" },
      };
  const { handleSubmit, ...form } = useForm({
    shouldUnregister: false,
    defaultValues,
    mode: "onChange",
  });

  const updateHarvester = useMutation({
    mutationFn: async (data) => {
      const { name, start_date, ...ui_state } = data.program;
      data.program = {
        name: data.program.name,
        start_date: data.program.start_date,
        ui_state,
        spec: ui_state,
      };
      return await api.patch(`/api/harvesters/${harvester.id}/`, data);
    },

    onSuccess: () => {
      onSave();
      queryClient.invalidateQueries({
        queryKey: ["harvesters"],
      });
      successToast({ message: "Updated harvesting rule" });
    },
  });

  const createHarvester = useMutation({
    mutationFn: async (data) => {
      const { name, start_date, ...ui_state } = data.program;
      data.program = {
        name: data.program.name,
        start_date: data.program.start_date,
        ui_state,
        spec: ui_state,
      };
      return await api.post(`/api/harvesters/`, data);
    },
    onSuccess: () => {
      onSave();
      queryClient.invalidateQueries({
        queryKey: ["harvesters"],
      });
      successToast({ message: "Created harvesting rule" });
    },
  });

  return (
    <FormProvider {...form} handleSubmit={handleSubmit}>
      <VStack gap={4} alignItems="start">
        <div className={css({ width: "50%" })}>
          <InputField
            label="Name"
            placeholder="Name"
            fieldName="program.name"
            errors={form.formState.errors}
            control={form.control}
            rules={{ required: { value: true, message: "Name is required" } }}
          />
          <HiddenField
            fieldName="program.start_date"
            control={form.control}
            rules={{ required: { value: true, message: "Start date is required" } }}
            defaultValue={dt}
          />
        </div>
        <Label>Sources</Label>
        <Controller
          name="sources"
          render={({ field }) => {
            return (
              <AdvertisingSelectorModal
                label="Select Harvesting Sources"
                selectedAdvertisementIds={field.value}
                onAdvertisementChange={(selected) => {
                  field.onChange(selected);
                }}
              />
            );
          }}
        />
        <Label>Destinations</Label>
        <Controller
          name="targets"
          render={({ field }) => {
            return (
              <AdvertisingSelectorModal
                label="Select Harvesting Destinations"
                selectedAdvertisementIds={field.value}
                onAdvertisementChange={(selected) => {
                  field.onChange(selected);
                }}
              />
            );
          }}
        />
        <div className={css({ width: "full" })}>
          <ProgramsForm {...form} hideHeader fieldName={"program"} />
        </div>
      </VStack>
      <HStack gap={4} justifyContent="end" marginTop={"2rem"}>
        <Button
          variant="secondary"
          onPress={() => {
            form.reset();
            onCancel();
          }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onPress={handleSubmit((data) =>
            harvester?.id ? updateHarvester.mutate(data) : createHarvester.mutate(data),
          )}
        >
          Save
        </Button>
      </HStack>
    </FormProvider>
  );
};

const LogsTable = ({ logs, categoryId = null }) => {
  const { adPlans } = useAds(categoryId);

  const adMap = useMemo(() => {
    return adPlans?.reduce((acc, ad) => {
      acc[ad.ad_id] = ad;
      return acc;
    }, {});
  }, [adPlans]);

  // flatten the logs, actions field has an array of objects
  const tableData = useMemo(() => {
    if (!logs) {
      return [];
    }
    return logs.reduce((acc, log) => {
      if (!log.action) {
        return acc;
      }
      const actions = log.action.map((action) => ({
        ...log,
        ...action,
        advertisementName: adMap?.[action.advertisement_id]?.ad_name ?? "",
      }));
      return [...acc, ...actions];
    }, []);
  }, [logs, adMap]);

  const colunmHelper = createColumnHelper();
  const columns = [
    colunmHelper.accessor("ts", {
      header: "Date",
      cell: (info) => moment(info.getValue()).format("ll"),
    }),
    //match_type
    colunmHelper.accessor("target", {
      header: "Target",
      size: 300,
      cell: (info) => info.getValue(),
    }),
    //target
    colunmHelper.accessor("match_type", {
      header: "Match Type",
      cell: (info) => info.getValue(),
    }),
    colunmHelper.accessor("advertisementName", {
      header: "Destination",
      size: 300,
      cell: (info) => {
        const id = info.row.original.advertisement_id;
        const name = info.getValue();
        return <TrellisLink to={`/user/advertising/dashboard/ads/${id}`}>{name}</TrellisLink>;
      },
    }),
  ];
  const table = useReactTable({
    data: tableData,
    columns,
    defaultColumn: {
      filterFn: () => true,
    },
    enableRowSelection: false,
    getCoreRowModel: getCoreRowModel(),
    rowCount: tableData.length || 0,
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 20,
      },
    },
  });

  return (
    <>
      <Flex justifyContent={"flex-end"}>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} canSetPageSize={false} />
      </Flex>
      <Table table={table} />
    </>
  );
};

const HarvestingRules = ({ advertisement = null }) => {
  const [selectedHarvester, setSelectedHarvester] = useState(null);
  const [createHarvester, setCreateHarvester] = useState(false);
  const [clone, setClone] = useState(null);
  const [showLogs, setShowLogs] = useState(false);

  const { data: selectedHarvesterData } = useFetch(
    ["harvesters", selectedHarvester],
    `/api/harvesters/${selectedHarvester}/`,
    {},
    { enabled: !!selectedHarvester, select: (res) => res.data ?? null },
  );

  const defaultHarvester = useMemo(() => {
    if (!selectedHarvester && !selectedHarvesterData) {
      return null;
    }
    if (selectedHarvesterData) {
      return selectedHarvesterData;
    }
  }, [selectedHarvester, selectedHarvesterData]);

  const { data: logs, isLoading: isLoadingLogs } = useFetch(
    ["harvester_logs", defaultHarvester?.program?.id],
    `/programslogs/`,
    { program_id: defaultHarvester?.program?.id, has_actions: true },
    { enabled: !!defaultHarvester, select: (res) => res.data?.results ?? [] },
  );

  const handleClone = (defaults) => {
    setClone(defaults);
    setCreateHarvester(true);
  };

  return (
    <>
      {!createHarvester && !selectedHarvester && (
        <VStack gap={4} justifyContent="start" alignItems={"start"}>
          <h3 className="fw-bold pb-4 pt-1 text">Custom Harvesting Rules</h3>
          <HarvestingRulesTable2 advertisement={advertisement} onClick={setSelectedHarvester} onClone={handleClone}>
            <Button onPress={() => setCreateHarvester(true)} fontSize={"small"}>
              Create
            </Button>
          </HarvestingRulesTable2>
        </VStack>
      )}
      {(createHarvester || (selectedHarvester && selectedHarvesterData)) && (
        <ProgramContextProvider engine={"harvesting"}>
          <HStack justifyContent={"space-between"} className={css({ width: "full", marginBottom: "2rem" })}>
            <HStack gap={4} justifyContent="start" className={`fs-standard ${css({ width: "full" })}`}>
              <Button
                variant="link"
                onPress={() => {
                  setCreateHarvester(false);
                  setSelectedHarvester(null);
                }}
              >
                Harvesting Rules
              </Button>
              / <b>{createHarvester ? "Create" : "Update"} Harvesting Rules</b>
            </HStack>
            {selectedHarvester && (
              <ButtonGroup
                compact
                value={showLogs ? "Logs" : "Edit"}
                onSelect={(label) => {
                  if (label === "Logs") {
                    setShowLogs(true);
                  } else {
                    setShowLogs(false);
                  }
                }}
                buttons={[
                  {
                    label: "Edit",
                    value: "Edit",
                  },
                  {
                    label: "Logs",
                    value: "Logs",
                  },
                ]}
              />
            )}
          </HStack>
          {!showLogs && (
            <HarvestingRulesForm
              source={advertisement}
              harvester={defaultHarvester || clone}
              create={createHarvester}
              onCancel={() => {
                setCreateHarvester(false);
                setSelectedHarvester(null);
                setClone(null);
              }}
              onSave={() => {
                setCreateHarvester(false);
                setSelectedHarvester(null);
                setClone(null);
              }}
            />
          )}
          {selectedHarvester && showLogs && (
            <div className={css({ width: "full" })}>
              <LogsTable logs={logs} isLoading={isLoadingLogs} />
            </div>
          )}
        </ProgramContextProvider>
      )}
    </>
  );
};

export default HarvestingRules;
